export default `
      <h1>Wanderer, 2025</h1>
      <p> Wanderer is an autonomous, digital flâneur, an internet-based entity that roams the map of our physical
        world through an endless sequence of directional choices, recorded immutably on the blockchain and visualized
        through web-based map services.</p>
      <p>Drawing on the Situationist practice of the dérive, the act of drifting through urban environments guided by
        emotion and chance, this work reimagines the wandering artist as a persistent algorithmic agent, animated not
        by human will but by server logic and blockchain transactions.</p>
      <p>While walking, Wanderer reflects on the early promise of the Internet as an open and free space, at a time
        when that openness is increasingly being enclosed.</p>

      <h2>About</h2>
      <p>Wanderer is a multilayered project.<br>
        At its core is a server application that walks across OpenStreetMap. It has its own crypto wallet and, at
        every junction, sends a 0 TIA transaction to itself. The resulting transaction hash, a unique string of
        characters, functions as the throw of the dice, determining the next turn. And so, Wanderer moves endlessly
        into the world.</p>
      <p>At Paris Photo 2025 at the Office Impart booth, the exhibition version will be presented as a dual-channel
        work of net art:<br>
        One screen shows Wanderer walking live on the world map.<br>
        The other recites its route in navigation blurbs, “Go left,” “Passing a church,” “Move straight,” “Walking on
        asphalt”, as a continuous, algorithmic poem.</p>
      <p>A second layer exists as a smart contract on Forma, enabling online viewers to collect junction points as
        NFTs. Each NFT is a unique on-chain SVG poem, composed of navigation blurbs from the chosen location,
        intertwined with a generative typographic ornament that visually “wanders.”
        Each junction can only be minted once.</p>
      <p>Wanderer begins its journey inside the Grand Palais at the opening of Paris Photo 2025, and will continue to
        wander for as long as the technology allows.</p>

      <h2>When, price, and collection size</h2>
      <p>Wanderer will begin walking at 5 PM on 11 November 2025.<br>
        Wanderer is premiering at my solo presentation with <a href="https://officeimpart.com/" target="_blank">Office
          Impart</a> at Paris Photo 2025 held at the Grand Palais in Paris. The launch will be initiated and
        celebrated with a drink at the booth and simultaneously go live online for the world.<br>
        Each NFT costs 1 TIA + gas fees.<br>
        Wanderer is an open edition: there is no cap on the number of NFTs, though each junction can only be collected
        once.</p>

      <h2>TIA</h2>
      <p>To pay with TIA, simply use your regular Ethereum wallet.<br>
        You can exchange ETH for TIA at <a href="https://bridge.forma.art/" target="_blank">bridge.forma.art</a>.<br>
        Your TIA will appear in your Ethereum wallet in MetaMask, just switch your network from Ethereum Mainnet to
        Forma.</p>

      <h2>How minting works</h2>
      <p>Visit <a href="https://wanderer.leegte.org/">wanderer.leegte.org</a> to follow Wanderer live at any time.<br>
        Connect your wallet via MetaMask (desktop or mobile).<br>
        Zoom out to see the full scope of the voyage.<br>
        Choose your favourite area and zoom in until you see the small blue junction icons.<br>
        Click or tap a junction to open its balloon.<br>
        Click or tap Mint to claim it.<br>
        When minting is complete, the junction will change to a red marker to embed your NFT.<br>
        You can also view your NFT on <a href="https://modularium.art/" target="_blank">modularium.art</a>.
      </p>

      <h2>How the work was made</h2>
      <p>The idea began with imagining an autonomous internet agent capable of wandering the world.</p>
      <p>The work consists of a server-based application that communicates with a crypto wallet. By sending 0 TIA
        transactions containing GPS coordinates in their metadata, the entire walk is immutably logged on the
        blockchain. The transaction hash from each step acts as a pseudo-random generator that determines the next
        move.</p>
      <p>This makes the path deterministic: if the walk were replayed using the wallet’s transaction history, it would
        retrace exactly the same journey.</p>
      <p>The system was developed by <a href="https://jaspervanloenen.com/" target="_blank">Jasper van Loenen</a> and
        <a href="http://www.bramdegroot.com/" target="_blank">Bram de Groot</a>.
      </p>

      <h2>Community</h2>
      <p>Join the <a href="https://discord.gg/y7aBJYpSh5" target="_blank">Leegte Discord server</a> to meet other
        collectors, discuss Wanderer and earlier collections, and stay informed about new works.</p>

      <h2>About the artist</h2>
      <p>Since the late 1990s, I have been creating art on the Internet, websites, apps, installations, videos,
        prints, sculptures, audio works, and drawings. The networked computer has always been my muse, a lens through
        which I explore the peculiarities and poetry of the digital world.</p>
      <p>My work has been exhibited at Whitechapel Gallery, Centre Pompidou, Stedelijk Museum Amsterdam, Van Gogh
        Museum, ZKM Karlsruhe, and Ludwig Museum, and is held in both private and institutional collections.</p>
      <p>This is my twelfth NFT drop.<br>
        Visit <a href="https://www.leegte.org/" target="_blank">www.leegte.org</a><br> to explore my work.
        Represented by <a href="https://www.upstreamgallery.nl/" target="_blank">Upstream Gallery</a><a> (Amsterdam)
          and </a><a href="https://officeimpart.com/" target="_blank">Office Impart</a> (Berlin).</p>

      <h2>Acknowledgements</h2>
      <p>First of all, thank you Office Impart to honor me with a solo presentation at Paris Photo!</p>
      <p>Endless gratitude to Jasper van Loenen and Bram de Groot for transforming a simple idea into a living system,
        navigating the intricacies of map APIs, algorithms, and all the complexity that is concealed behind this
        minimal work.</p>
      <p>Special thanks to Sam Spike and Achilleas from Forma / Celestia for the invitation to realize this project on
        their blockchain, and to the Forma team for their smart contract development and support.</p>
`;
